import {
    createEmptyProduct as createBaseProduct,
    createEmptyTaxLine,
} from 'composables/useOcrProducts';

// Auto-filling payments detected by the OCR is disabled (SAAS-32702). The mapping
// below is kept intact so it can be re-enabled when the user-facing toggle is added.
const AUTO_FILL_PAYMENTS_FROM_OCR = false;

const createProduct = (options = {}) => {
    return { ...createBaseProduct(), ...options };
};

const isDefined = (data, key) => {
    return data[key] !== undefined && data[key].value !== 'N/A';
};

const findTaxIdByRate = (taxList, rate) => {
    return taxList.find((tax) => parseFloat(tax.rate) === parseFloat(rate))?.id ?? null;
};

const getDefaultAccountCode = (accountingCodes, defaultAccountingCode) => {
    return accountingCodes.find((code) => code.id === defaultAccountingCode);
};

const formatDateString = (dateValue) => {
    if (!dateValue || dateValue.length === 0) {
        return '';
    }
    return dateValue.split(/[T ]/)[0];
};

const fieldValue = (field, fallback = null) => {
    if (field === null || field === undefined) {
        return fallback;
    }
    const val = field.value ?? field;
    return val === null || val === undefined ? fallback : val;
};

const findPaymentTypeId = (paymentTypes, ocrMethod) => {
    if (!paymentTypes?.length || !ocrMethod) {
        return null;
    }
    const normalized = ocrMethod.toLowerCase().trim();
    const match = paymentTypes.find((type) => {
        return type.label.toLowerCase().trim() === normalized;
    });
    return match?.id ?? null;
};

const formatAmount = (value, decimals = 2) => {
    if (value === null || value === undefined) {
        return null;
    }
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) {
        return null;
    }
    return parsed.toFixed(decimals).replace('.', ',');
};

const createTaxLine = (overrides) => {
    return { ...createEmptyTaxLine(), ...overrides };
};

const hydrateTaxLines = (taxes, params, baseOptions) => {
    if (!Array.isArray(taxes) || taxes.length === 0) {
        return [createTaxLine({ ...baseOptions })];
    }
    return taxes.map((tax) =>
        createTaxLine({
            ...baseOptions,
            taxRateId: findTaxIdByRate(params.taxList, tax.rate),
            amountExcludingTax: formatAmount(tax.base),
            amountIncludingTax: formatAmount(tax.total),
        }),
    );
};

const hydrateCatalogueLink = (item) => {
    const link = item.catalogue_link;
    if (!link?.id) {
        return null;
    }
    return { id: link.id, label: link.label, value: link.id };
};

const hydrateLineItem = (item, params, baseOptions) => {
    const quantity = parseFloat(fieldValue(item.quantity, 1)) || 1;
    const unitPrice = fieldValue(item.unit_price);
    const taxRate = fieldValue(item.tax_rate);
    const taxId = taxRate !== null ? findTaxIdByRate(params.taxList, taxRate) : null;
    const analyticCode = item.analytics_code?.value ?? '';
    const catalogueLink = hydrateCatalogueLink(item);
    const description =
        fieldValue(item.description) ||
        fieldValue(item.product_code) ||
        catalogueLink?.label ||
        baseOptions.description ||
        '';
    const accountingCode = item.accounting_code ?? baseOptions.accountingCode ?? null;

    return createProduct({
        ...baseOptions,
        description,
        quantity,
        priceWithoutTax: formatAmount(unitPrice, params.decimals),
        taxRateId: taxId,
        analyticCode,
        catalogueLink,
        accountingCode,
    });
};

export const hydrateOcr = (data, params) => {
    const accountCode = getDefaultAccountCode(params.accountingCodes, params.defaultAccountingCode);

    const baseProductOptions = {
        description: accountCode?.label ?? '',
        accountingCode: accountCode ? params.defaultAccountingCode : null,
    };

    const hasSupplier = data.supplier?.id !== null && data.supplier?.id !== undefined;
    const invoiceDate = isDefined(data, 'invoice_date') ? data.invoice_date.value : '';
    const dueDate = isDefined(data, 'due_date') ? data.due_date.value : '';

    const lineItems = data.line_items;
    const hasLineItems = Array.isArray(lineItems) && lineItems.length > 0;

    let analyticsMode = null;
    if (data.analytics_code !== undefined) {
        analyticsMode = 'doc';
    } else if (hasLineItems && lineItems[0].analytics_code !== undefined) {
        analyticsMode = 'item';
    }

    const ocr = {
        supplier: hasSupplier ? +data.supplier.id : null,
        documentDate: formatDateString(invoiceDate),
        dueDate: formatDateString(dueDate || invoiceDate),
        currency: isDefined(data, 'locale') ? data.locale.value : '',
        totalWithoutTax: isDefined(data, 'total_excl') ? data.total_excl.value : null,
        totalWithTax: isDefined(data, 'total_incl') ? data.total_incl.value : null,
        externalDocumentName: isDefined(data, 'invoice_number') ? data.invoice_number.value : null,
    };

    const rawPayments = data.payments;
    if (AUTO_FILL_PAYMENTS_FROM_OCR && Array.isArray(rawPayments) && rawPayments.length > 0) {
        const payments = [];

        for (const payment of rawPayments) {
            const dateStr = payment.date;
            if (!dateStr || dateStr === 'N/A') {
                continue;
            }

            payments.push({
                date: dateStr,
                reference: '',
                amount: payment.amount ? formatAmount(payment.amount) : null,
                payment_mean: findPaymentTypeId(params.paymentTypes, payment.method) ?? '',
            });
        }

        if (payments.length > 0) {
            ocr.payments = payments;
        }
    }

    if (analyticsMode === 'doc') {
        ocr.analyticCode = data.analytics_code?.value ?? '';
    }

    if (hasLineItems) {
        ocr.products = lineItems.map((item) => hydrateLineItem(item, params, baseProductOptions));
    } else if (isDefined(data, 'taxes')) {
        const taxes = data.taxes;

        if (taxes.length <= 1) {
            const taxId =
                taxes.length === 1 ? findTaxIdByRate(params.taxList, taxes[0].rate) : null;
            ocr.products = [
                createProduct({
                    ...baseProductOptions,
                    taxRateId: taxId,
                    priceWithoutTax: formatAmount(ocr.totalWithoutTax, params.decimals),
                }),
            ];
        } else {
            ocr.products = taxes.map((tax) =>
                createProduct({
                    ...baseProductOptions,
                    taxRateId: findTaxIdByRate(params.taxList, tax.rate),
                }),
            );
        }
    }

    ocr.lineMode = 'product';
    ocr.taxLines = hydrateTaxLines(data.taxes, params, baseProductOptions);

    return { formData: ocr, analyticsMode };
};

export { createProduct };
